import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  TableChart as ExcelIcon,
  Code as HtmlIcon,
  Download as DownloadIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { adminApi, Organization } from '../services/api';

function getRecentPeriods(count: number): string[] {
  const periods: string[] = [];
  const now = new Date();
  
  for (let i = 0; i < count; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    periods.push(`${year}-${month}`);
  }
  
  return periods;
}

interface ExportJob {
  jobId: string;
  status: 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  progress: number;
  currentStep: string;
  downloadUrl?: string;
  errorMessage?: string;
}

const ExportPage: React.FC = () => {
  const [period, setPeriod] = useState(getRecentPeriods(1)[0]);
  const [organizationId, setOrganizationId] = useState('');
  const [includeDisabled, setIncludeDisabled] = useState(true);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Job tracking state
  const [currentJob, setCurrentJob] = useState<ExportJob | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const periods = getRecentPeriods(3); // Limited to 3 months due to 90-day data retention

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const response = await adminApi.listOrganizations();
        if (response.success) {
          setOrganizations(response.data.organizations);
        }
      } catch (err) {
        console.error('Failed to load organizations:', err);
      }
    };
    fetchOrgs();

    // Cleanup polling on unmount
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const pollJobStatus = async (jobId: string) => {
    try {
      const response = await adminApi.getExportJobStatus(jobId);
      
      if (response.success) {
        const job = response.data as ExportJob;
        setCurrentJob(job);

        if (job.status === 'COMPLETED') {
          // Stop polling
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          
          setExporting(null);
          setSuccess('Export completed! Download started.');
          
          // Auto-download
          if (job.downloadUrl) {
            window.open(job.downloadUrl, '_blank');
          }
          
          // Clear job after a delay
          setTimeout(() => setCurrentJob(null), 5000);
        } else if (job.status === 'FAILED') {
          // Stop polling
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          
          setExporting(null);
          setError(job.errorMessage || 'Export failed');
          setCurrentJob(null);
        }
      }
    } catch (err: any) {
      console.error('Failed to poll job status:', err);
      // Don't stop polling on transient errors
    }
  };

  const handleExport = async (format: 'excel' | 'pdf') => {
    try {
      setExporting(format);
      setError(null);
      setSuccess(null);
      setCurrentJob(null);

      const params = {
        period,
        organizationId: organizationId || undefined,
        includeDisabled,
      };

      let response;
      if (format === 'excel') {
        response = await adminApi.exportUsersExcel(params);
      } else {
        response = await adminApi.exportUsersPdf({
          ...params,
          reportTitle: `User Usage Report - ${period}`,
        });
      }

      if (response.success && response.data.jobId) {
        const jobId = response.data.jobId;
        
        // Set initial job state
        setCurrentJob({
          jobId,
          status: 'QUEUED',
          progress: 0,
          currentStep: 'Job queued...'
        });

        // Start polling every 2 seconds
        pollingRef.current = setInterval(() => pollJobStatus(jobId), 2000);
        
        // Also poll immediately
        pollJobStatus(jobId);
      } else if (response.success && response.data.downloadUrl) {
        // Fallback for synchronous response (shouldn't happen with new code)
        window.open(response.data.downloadUrl, '_blank');
        setSuccess(`${format.toUpperCase()} export generated successfully! Download started.`);
        setExporting(null);
      } else {
        setError('Export failed - no job ID received');
        setExporting(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Export failed');
      setExporting(null);
      setCurrentJob(null);
    }
  };

  const formatStepMessage = (step: string, progress: number) => {
    if (step) return step;
    if (progress < 10) return 'Starting export...';
    if (progress < 50) return 'Fetching users...';
    if (progress < 80) return 'Processing data...';
    if (progress < 95) return 'Generating file...';
    return 'Finishing up...';
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Export Data
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Export user data and usage statistics to Excel or PDF format.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Job Progress Card */}
      {currentJob && (
        <Card sx={{ mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              {currentJob.status === 'COMPLETED' ? (
                <SuccessIcon color="success" />
              ) : (
                <CircularProgress size={24} />
              )}
              <Typography variant="h6">
                {currentJob.status === 'COMPLETED' 
                  ? 'Export Complete!' 
                  : currentJob.status === 'PROCESSING' 
                    ? 'Processing Export...' 
                    : 'Queued for Processing...'}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 1 }}>
              <LinearProgress 
                variant="determinate" 
                value={currentJob.progress} 
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              {formatStepMessage(currentJob.currentStep, currentJob.progress)} ({currentJob.progress}%)
            </Typography>

            {currentJob.status === 'COMPLETED' && currentJob.downloadUrl && (
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={() => window.open(currentJob.downloadUrl, '_blank')}
                sx={{ mt: 2 }}
              >
                Download Again
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Export Options
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Period</InputLabel>
                <Select
                  value={period}
                  label="Period"
                  onChange={(e) => setPeriod(e.target.value)}
                  disabled={exporting !== null}
                >
                  {periods.map((p) => (
                    <MenuItem key={p} value={p}>
                      {new Date(p + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={organizationId}
                  label="Organization"
                  onChange={(e) => setOrganizationId(e.target.value)}
                  disabled={exporting !== null}
                >
                  <MenuItem value="">All Organizations</MenuItem>
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.displayName || org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeDisabled}
                    onChange={(e) => setIncludeDisabled(e.target.checked)}
                    disabled={exporting !== null}
                  />
                }
                label="Include disabled users"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" fontWeight={600} gutterBottom>
            Export Format
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ExcelIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Excel Export
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Export data as .xlsx file with formatted columns and summary sheet.
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={exporting === 'excel' ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                    onClick={() => handleExport('excel')}
                    disabled={exporting !== null}
                    fullWidth
                  >
                    {exporting === 'excel' ? 'Processing...' : 'Export to Excel'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <HtmlIcon sx={{ fontSize: 48, color: 'info.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    HTML Report
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Generate a formatted HTML report that opens in browser. Use browser print to save as PDF.
                  </Typography>
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={exporting === 'pdf' ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                    onClick={() => handleExport('pdf')}
                    disabled={exporting !== null}
                    fullWidth
                  >
                    {exporting === 'pdf' ? 'Processing...' : 'Export HTML Report'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
            Note: Exports run in the background. Download links expire after 24 hours. Large exports may take up to a minute to complete.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ExportPage;
